import { License } from '../../Participants/AddParticipant/Stores/Types';
import { T } from '../../../../Translations';
import { availabilityOf } from '../../LicenseAvailability';
import AvailabilityBar from '../../AvailabilityBar';

interface Props {
  license: License;
  selected: boolean;
  onToggle: (licenseId: number, checked: boolean) => void;
}

/**
 * A single license term inside an expanded license-type card: a checkbox, the
 * term's name + date range, and its availability line. A single term is
 * either fully available or over-activated, so the availability display is a
 * two-way branch — there is no partial/low state for one term.
 */
function LicenseTermRow({ license, selected, onToggle }: Props) {
  const licenseName = license.licenseName.getOrElse(() => '');
  const dateRange = `${license.startOn.toLocaleDateString()} - ${license.endOn.toLocaleDateString()}`;
  const availability = availabilityOf(license.quantityAvailable);

  return (
    <label
      className={`flex items-start gap-3 p-3 border rounded-md transition-colors cursor-pointer bg-card ${
        selected ? 'border-accent' : 'border-border'
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={(e) => onToggle(license.id, e.target.checked)}
        className="w-5 h-5 mt-0.5 rounded cursor-pointer text-primary border-border focus:ring-primary"
      />
      <div className="flex-1 text-sm">
        <div className="mb-2">
          <span className="font-medium">
            {licenseName ? `${licenseName} (${dateRange})` : dateRange}
          </span>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-muted-foreground text-xs">
            <span>
              <T kind="Available" />
            </span>
            {availability.isOverActivated ? (
              <span className="font-semibold text-destructive">
                <T
                  kind="Over-activated by {{overActivated}} ({{overActivated}} of 0)"
                  overActivated={availability.overActivated}
                />
              </span>
            ) : (
              <span className="font-semibold">
                <T
                  kind="{{available}} of {{total}} available"
                  available={availability.available}
                  total={availability.capacity}
                />
              </span>
            )}
          </div>
          <AvailabilityBar availability={availability} size="sm" />
        </div>
      </div>
    </label>
  );
}

export default LicenseTermRow;
